import React, { useCallback, useEffect, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { updatePageModule, updateSession, updateWorkingSession } from '../../../../../store/actions/admin/create-event/session';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import { PageModule, PageModuleGroupModules, Session } from '../../../../../types/working-model';
import { hasPageModuleControls } from '../../../../../utils/controls-utils';
import Icon, { COLORS, ICONS } from '../../../../general-ui/icon';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import './controls.scss';

export interface InitialControlsDisplay {
	editColors?: boolean;
	addContent?: boolean;
	changeSectionPosition?: boolean;
}

const InitialControlsDisplay = {
	editColors: true,
	addContent: true,
	changeSectionPosition: true,
};

interface IControlsProps {
	page_module: PageModule;
	openEditModal: () => void;
	display?: InitialControlsDisplay;
}

const Controls: React.FC<React.PropsWithChildren<IControlsProps>> = (props) => {
	const {
		page_module,
		openEditModal,
		children,
		display = InitialControlsDisplay
	} = props;

	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const dispatch = useDispatch();
	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [workingPageModule, setWorkingPageModule] = useState(page_module);

	useEffect(() => setWorkingPageModule(page_module), [page_module]);

	function changeModuleIndex(direction: number, moveModule: PageModule): void {
		if (workingSession) {
			const updatedSession: Session = {
				...workingSession,
				module_grouping: workingSession.module_grouping?.map((module: PageModuleGroupModules) => {
					// find correct module_group
					const matched = module.modules.find((moduleId: number) => moduleId === moveModule.id);
					if (!matched) return module;
					const _modules = module.modules.slice();
					const startIndex = _modules.findIndex((moduleId: number) => moduleId === moveModule.id);
					// i is either 1 or -1 indicating move up or down in the list
					const canMoveUp = !startIndex ? direction !== -1 : true;

					// if start index is 0 and direction is up, leave the module where it is!
					const newIndex = canMoveUp ? startIndex + direction : startIndex;

					const [removedModule] = _modules.splice(startIndex, 1);
					_modules.splice(newIndex, 0, removedModule);
					// placeholder for now
					return {
						...module,
						modules: _modules
					};
				}),
			};

			batch(() => {
				// update db
				dispatch(updateSession(updatedSession, token!));
				// update redux
				dispatch(updateWorkingSession(updatedSession));
			});

		}
	}

	const makeBackgroundStylingOverrides = useCallback((colorKey: string) => {
		const isColor = !colorKey.includes('.');
		const isRestoringDefault = colorKey === "";

		//If we are restoreing default we want it to just be a blank string, if changing color we want the classname with appended"-background"
		if (isColor && !isRestoringDefault) {
			colorKey += "-background";
		}
		return {
			color: page_module?.styling_overrides?.background?.color ?? '',
			image: { is_on: false, src: page_module?.styling_overrides?.background?.image.src ?? '' },
			[isColor ? 'color' : 'image']: isColor ? colorKey : { is_on: true, src: colorKey }
		};
	}, [page_module?.styling_overrides?.background?.color, page_module?.styling_overrides?.background?.image.src]);

	const handleFinishStyling = useCallback((colorKey: string) => {
		const background = makeBackgroundStylingOverrides(colorKey);

		if (hasPageModuleControls(page_module)) {
			// dispatch update session module here 
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: {
						...page_module.styling_overrides,
						background
					}
				})
			);
		}
		setOpenStyleModal(false);
	}, [dispatch, makeBackgroundStylingOverrides, page_module]);

	const handleCancelStyling = useCallback(() => {
		setOpenStyleModal(false);
	}, []);

	return (
		<div className={`session-module-controls ${children ? 'has-children' : ''}`}>
			<div className="button-container">
				{
					display.editColors && (
						<button
							className="round"
							onClick={() => setOpenStyleModal(true)}
						>
							<Icon name={ICONS.PAINTBRUSH} size={12} color={COLORS.WHITE} />
						</button>
					)
				}
				{
					display.addContent && (
						<button className="button round" onClick={() => openEditModal()}>
							<Icon
								name={ICONS.EDIT}
								color={COLORS.WHITE}
								size={12}
								backgroundColor={COLORS.DARK_GREY}
							/>
						</button>
					)
				}
				{
					display.changeSectionPosition && (
						<>
							<button className="button round arrow-up" onClick={() => changeModuleIndex(-1, page_module)} >
								<Icon
									name={ICONS.ARROW_UP_THIN}
									color={COLORS.WHITE}
									size={12}
									backgroundColor={COLORS.DARK_GREY}
								/>
							</button>
							<button className="button round arrow-down" onClick={() => changeModuleIndex(1, page_module)}>
								<Icon
									name={ICONS.ARROW_UP_THIN}
									color={COLORS.WHITE}
									size={12}
									backgroundColor={COLORS.DARK_GREY}
								/>
							</button>
						</>
					)
				}
				{/* 
        el says no to this, keeping it just in case
        <button className="button round">
          <Icon
            name={ICONS.DELETE}
            color={COLORS.WHITE}
            size={12}
            backgroundColor={COLORS.DARK_GREY}
          />
        </button>
        */}
			</div>
			{children}
			<EditSectionColorsModal
				open={openStyleModal}
				title={EContentColorOverrideNames.background}
				backgroundImage={workingPageModule?.styling_overrides?.background?.image.src}
				onFinish={handleFinishStyling}
				onClose={handleCancelStyling}
			/>
		</div>
	);
};

export default Controls;
